import React, { useEffect, useState, useMemo, useCallback } from 'react';
import http from '../api/http';
import { DatePicker, Input, InputNumber, Button, Space, Upload, Progress, message, Modal, Tooltip, Select, Drawer, Tabs, Card, Statistic, Row, Col, Table, Tag, Alert } from 'antd';
import dayjs from 'dayjs';
import { openWithAuth } from '../utils/authedResource';
import './WorkPlansPage.css';

const WorkPlansPage = () => {
  // 列表与筛选相关状态
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [selectedUser, setSelectedUser] = useState(undefined);
  const [keyword, setKeyword] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  // 行内编辑已移除，改为弹窗编辑
  // const [inlineEditing, setInlineEditing] = useState({});
  // 新增计划弹窗
  const [form, setForm] = useState({ plan_text:'', planned_completion_date: dayjs().format('YYYY-MM-DD'), last_month_completion:'', progress_percent:0, remark:'' });
  const [createVisible, setCreateVisible] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  // 进度编辑
  // 进度编辑并入弹窗
  // 编辑计划弹窗
  const [editVisible, setEditVisible] = useState(false);
  const [editForm, setEditForm] = useState({ plan_text:'', planned_completion_date: dayjs().format('YYYY-MM-DD'), last_month_completion:'', progress_percent:0, remark:'' });
  const [editFiles, setEditFiles] = useState([]);
  const [editUploading, setEditUploading] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [editPhotos, setEditPhotos] = useState([]);
  // 用户权限
  const [userPerms, setUserPerms] = useState([]);
  const [userId, setUserId] = useState(null);
  // 看板
  const [activeTab, setActiveTab] = useState('list');
  const [boardLoading, setBoardLoading] = useState(false);
  const [boardStats, setBoardStats] = useState({ total:0, completed:0, overdue:0, avg:0 });
  const [boardPerUser, setBoardPerUser] = useState([]);
  const [boardTopIncomplete, setBoardTopIncomplete] = useState([]);
  // 批量更新
  const [bulkVisible, setBulkVisible] = useState(false);
  const [bulkMode, setBulkMode] = useState('set');
  const [bulkValue, setBulkValue] = useState(0);
  // 附件查看
  const [photoModal, setPhotoModal] = useState({ open:false, photos:[], planId:null });
  // 申请管理
  const [requests, setRequests] = useState([]);
  const [requestModal, setRequestModal] = useState({ visible: false, type: '', planId: null, details: { new_date: '', new_user_id: '' } });
  const [approvalModal, setApprovalModal] = useState({ visible: false, requestId: null, action: '' });

  // 初始化用户权限
  useEffect(()=>{
    try {
      const u = JSON.parse(localStorage.getItem('user'));
      if(u){ setUserPerms(u.permissions||[]); setUserId(u.id); }
    } catch(e){ /* ignore */ }
  },[]);

  // 加载用户列表（用于筛选）
  const loadUsers = async () => {
    try {
      const { data } = await http.get('/work-plans/users');
      if(data.success) setUsers(data.users||[]);
    } catch(e){ /* 无权限忽略 */ }
  };
  useEffect(()=>{ loadUsers(); }, []);

  // 申请管理相关函数
  const loadRequests = useCallback(async () => {
    try {
      const { data } = await http.get('/work-plans/requests');
      if(data.success) setRequests(data.requests||[]);
    } catch(e){ message.error(e.response?.data?.error || e.message || '加载申请失败'); }
  }, []);

  const submitRequest = async () => {
    if(!requestModal.planId || !requestModal.type) return;
    const body = { type: requestModal.type, details: requestModal.details };
    try {
      const { data } = await http.post(`/work-plans/${requestModal.planId}/requests`, body);
      if(!data.success) return message.error(data.error||'提交申请失败');
      message.success('申请已提交');
      setRequestModal({ visible: false, type: '', planId: null, details: { new_date: '', new_user_id: '' } });
      if(activeTab==='requests') loadRequests();
    } catch(e){ message.error(e.response?.data?.error || e.message || '提交申请失败'); }
  };

  const approveRequest = async (requestId, action) => {
    try {
      const { data } = await http.patch(`/work-plans/requests/${requestId}`, { action });
      if(!data.success) return message.error(data.error||'审核失败');
      message.success(action==='approve'?'已批准':'已拒绝');
      setApprovalModal({ visible: false, requestId: null, action: '' });
      loadRequests();
    } catch(e){ message.error(e.response?.data?.error || e.message || '审核失败'); }
  };

  const canApprove = userPerms.includes('work_plans_manage') || userPerms.includes('project_manager') || userPerms.includes('deputy_manager');

  // 加载计划列表
  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if(selectedUser) params.user_id = selectedUser;
      if(keyword.trim()) params.keyword = keyword.trim();
      const { data } = await http.get('/work-plans', { params });
      if(data.success) setPlans(data.records||[]);
    } catch(e){ message.error(e.response?.data?.error || e.message || '加载失败'); }
    finally { setLoading(false); }
  }, [month, selectedUser, keyword]);
  useEffect(()=> { if(activeTab==='list') loadPlans(); }, [activeTab, loadPlans]);

  // 看板统计计算
  const computeBoardStats = (rec) => {
    const total = rec.length;
    let sum=0, completed=0, overdue=0;
    const today = dayjs().format('YYYY-MM-DD');
    const userMap = {};
    rec.forEach(r=>{
      const prog = Number(r.progress_percent)||0;
      sum += prog;
      if(prog >= 100) completed++;
      if(prog < 100 && r.planned_completion_date < today) overdue++;
      const uid = r.user_id || r.user_name;
      if(!userMap[uid]) userMap[uid] = { user_name: r.user_name||'未知', count:0, completed:0, sumProg:0, max_date: r.planned_completion_date };
      const u = userMap[uid];
      u.count++; u.sumProg += prog; if(prog>=100) u.completed++; if(r.planned_completion_date > u.max_date) u.max_date = r.planned_completion_date;
    });
    const avg = total? sum/total : 0;
    const perUserArr = Object.values(userMap).map(u=> ({ ...u, avg_progress: u.count? u.sumProg/u.count : 0 }));
    perUserArr.sort((a,b)=> b.avg_progress - a.avg_progress);
    const topIncompleteArr = rec.filter(r=> (r.progress_percent||0) < 100).sort((a,b)=> (a.progress_percent||0)-(b.progress_percent||0)).slice(0,10);
    setBoardStats({ total, completed, overdue, avg });
    setBoardPerUser(perUserArr);
    setBoardTopIncomplete(topIncompleteArr);
  };

  const loadBoardData = useCallback(async () => {
    setBoardLoading(true);
    try {
      const { data } = await http.get('/work-plans', { params:{ month } });
      if(!data.success) throw new Error(data.error||'加载失败');
      computeBoardStats(data.records||[]);
    } catch(e){ message.error(e.message||'看板加载失败'); }
    finally { setBoardLoading(false); }
  }, [month]);
  useEffect(()=>{ if(activeTab==='board') loadBoardData(); }, [activeTab, loadBoardData]);
  useEffect(()=>{ if(activeTab==='requests') loadRequests(); }, [activeTab, loadRequests]);

  // ==== 恢复之前被替换丢失的业务函数 ====
  const canManage = userPerms.includes('work_plans_manage');

  // 新增计划提交
  const submitPlan = async () => {
    if(!form.plan_text.trim()) return message.warning('请填写工作计划内容');
    try {
      const { data } = await http.post('/work-plans', form);
      if(!data.success) return message.error(data.error||'创建失败');
      const id = data.id;
      if(uploadFiles.length){
        setUploading(true);
        const fd = new FormData();
        uploadFiles.forEach(f=> fd.append('photos', f.originFileObj));
        await http.post(`/work-plans/${id}/photos`, fd, { headers: { 'Content-Type':'multipart/form-data' } });
        setUploading(false);
      }
      message.success('工作计划已提交');
      setForm({ plan_text:'', planned_completion_date: dayjs().format('YYYY-MM-DD'), last_month_completion:'', progress_percent:0, remark:'' });
      setUploadFiles([]);
      setCreateVisible(false);
      if(activeTab==='list') loadPlans(); else loadBoardData();
    } catch(e){ message.error(e.response?.data?.error || e.message || '提交失败'); setUploading(false); }
  };

  // 编辑弹窗相关
  const openEdit = async (plan) => {
    setCurrentEditId(plan.id);
    setEditForm({
      plan_text: plan.plan_text||'',
      planned_completion_date: plan.planned_completion_date ? dayjs(plan.planned_completion_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      last_month_completion: plan.last_month_completion||'',
      progress_percent: plan.progress_percent||0,
      remark: plan.remark||''
    });
    setEditFiles([]);
    try {
      const { data } = await http.get(`/work-plans/${plan.id}/photos`);
      if(data.success) setEditPhotos(data.photos||[]); else setEditPhotos([]);
    } catch(e){ setEditPhotos([]); }
    setEditVisible(true);
  };

  const saveEditPlan = async () => {
    if(!currentEditId) return;
    if(!editForm.plan_text.trim()) return message.warning('计划内容不能为空');
    try {
      const patchBody = { ...editForm };
      const { data } = await http.patch(`/work-plans/${currentEditId}`, patchBody);
      if(!data.success) return message.error(data.error||'保存失败');
      if(editFiles.length){
        setEditUploading(true);
        const fd = new FormData();
        editFiles.forEach(f => fd.append('photos', f.originFileObj));
        await http.post(`/work-plans/${currentEditId}/photos`, fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      }
      setPlans(plans.map(p => p.id===currentEditId ? { ...p, ...editForm } : p));
      message.success('已保存');
      setEditUploading(false);
      setEditVisible(false);
      if(activeTab==='board') loadBoardData();
    } catch(e){ message.error(e.response?.data?.error || e.message || '保存失败'); setEditUploading(false); }
  };

  // 选择与批量更新
  const clearSelection = () => setSelectedIds([]);
  const doBulkUpdate = async () => {
    if(!selectedIds.length) return message.warning('未选择任何计划');
    try {
      const body = { ids: selectedIds, mode: bulkMode, value: bulkValue };
      const { data } = await http.post('/work-plans/bulk-progress', body);
      if(!data.success) return message.error(data.error||'批量更新失败');
      message.success(`批量更新成功 (${data.affected})`);
      setBulkVisible(false); clearSelection(); loadPlans(); if(activeTab==='board') loadBoardData();
    } catch(e){ message.error(e.response?.data?.error || e.message || '批量更新失败'); }
  };

  // 附件与导出
  const openPhotos = async (planId) => {
    try {
      const { data } = await http.get(`/work-plans/${planId}/photos`);
      if(data.success) setPhotoModal({ open:true, photos:data.photos||[], planId });
    } catch(e){ message.error(e.response?.data?.error || e.message || '读取附件失败'); }
  };
  const exportExcel = async () => {
    try {
      const res = await http.get('/work-plans/export', { params:{ month }, responseType:'blob' });
      const blob = new Blob([res.data], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `work_plans_${month}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch(e){ message.error(e.response?.data?.error || e.message || '导出失败'); }
  };
  // ==== 恢复结束 ====

  // 计算 Table 数据源（可在这里附加状态标签等）
  const tableData = useMemo(()=> {
    const today = dayjs();
    return plans.map(p => {
      const prog = Number(p.progress_percent)||0;
      const planned = dayjs(p.planned_completion_date);
      const cancelled = p.status === 'cancelled';
      const overdue = !cancelled && prog < 100 && planned.isBefore(today, 'day');
      const dueSoon = !cancelled && prog < 100 && !overdue && (planned.isSame(today, 'day') || (planned.isBefore(today.add(4, 'day'), 'day') && planned.isAfter(today.subtract(1, 'day'), 'day')));
      let statusLabel = '进行中';
      if(cancelled) statusLabel = '取消';
      else if(prog >= 100) statusLabel = '完成';
      else if(overdue) statusLabel = '逾期';
      else if(dueSoon) statusLabel = '临近';
      return { ...p, _progress: prog, _status: statusLabel, _overdue: overdue, _dueSoon: !!dueSoon, _cancelled: cancelled };
    });
  }, [plans]);

  const summaryStats = useMemo(()=> {
    let overdue=0, dueSoon=0; tableData.forEach(r=> { if(r._overdue) overdue++; else if(r._dueSoon) dueSoon++; });
    return { overdue, dueSoon };
  }, [tableData]);

  const canEditRow = (p) => userPerms.includes('work_plans_manage') || (userPerms.includes('work_plans_view') && p.user_id === userId);

  const columns = [
    {
      title: '选择',
      dataIndex: 'select',
      width: 60,
      render: (_, record) => (
        <input
          type='checkbox'
          checked={selectedIds.includes(record.id)}
          onChange={() => {
            setSelectedIds(prev => prev.includes(record.id) ? prev.filter(x=>x!==record.id) : [...prev, record.id]);
          }}
        />
      )
    },
    { title: '用户', dataIndex: 'user_name', width: 110 },
    { title: '计划完成日期', dataIndex: 'planned_completion_date', width: 120, render: v => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    {
      title: '内容',
      dataIndex: 'plan_text',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={<span style={{ whiteSpace:'pre-wrap' }}>{text}</span>}><span style={{ whiteSpace:'pre-wrap' }}>{text}</span></Tooltip>
      )
    },
    {
      title: '补充信息',
      dataIndex: 'last_month_completion',
      width: 260,
      render: (_, record) => (
        <div style={{ fontSize:12 }}>
          {record.last_month_completion && <div style={{ color:'#555' }}>上月: {record.last_month_completion}</div>}
          {record.remark && <div style={{ color:'#555' }}>备注: {record.remark}</div>}
          {record.status==='cancelled' && record.cancelled_at && <div style={{ color:'#c00' }}>取消: {dayjs(record.cancelled_at).format('YYYY-MM-DD')} ({record.cancelled_by_name||'审核人'})</div>}
          {record.status==='active' && record.reactivated_at && <div style={{ color:'#0a7' }}>恢复: {dayjs(record.reactivated_at).format('YYYY-MM-DD')} ({record.reactivated_by_name||'审核人'})</div>}
        </div>
      )
    },
    {
      title: '进度与状态',
      dataIndex: '_progress',
      width: 200,
      render: (_, record) => {
        // 取消状态时不显示进度条百分比，直接显示标签
        if(record._cancelled){
          return <Tag color='default'>已取消</Tag>;
        }
        const barClass = record._overdue ? 'wp-progress-bar-wrapper wp-progress-overdue' : 'wp-progress-bar-wrapper';
        return (
          <div className='wp-progress-cell'>
            <div className={barClass}>
              <Progress percent={record._progress} size='small' status={record._progress===100?'success':'active'} />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:11, color:'#555' }}>{record._progress}%</span>
              <span className={`wp-status-tag ${record._status==='完成'?'completed': record._status==='逾期'?'overdue': record._status==='临近'?'dueSoon':'active'}`}>{record._status}</span>
            </div>
          </div>
        );
      }
    },
    {
      title: '操作',
      dataIndex: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size={4} wrap>
          <Button size='small' onClick={()=> openEdit(record)} disabled={!canEditRow(record) || record.status==='cancelled'}>编辑</Button>
          <Button size='small' className='wp-attach-btn' onClick={()=> openPhotos(record.id)}>附件({record.photo_count||0})</Button>
          {record.status!=='cancelled' && (
            <>
              <Button size='small' type='link' onClick={()=> setRequestModal({ visible: true, type: 'delay', planId: record.id, details: { new_date: record.planned_completion_date, new_user_id: '' } })}>申请延期</Button>
              <Button size='small' type='link' onClick={()=> setRequestModal({ visible: true, type: 'cancel', planId: record.id, details: { new_date: '', new_user_id: '' } })}>申请取消</Button>
              <Button size='small' type='link' onClick={()=> setRequestModal({ visible: true, type: 'transfer', planId: record.id, details: { new_date: '', new_user_id: '' } })}>申请转移</Button>
            </>
          )}
          {record.status==='cancelled' && (
            <Button size='small' type='link' onClick={()=> setRequestModal({ visible: true, type: 'reactivate', planId: record.id, details: { new_date: '', new_user_id: '' } })}>申请恢复</Button>
          )}
        </Space>
      )
    },
    {
      title: '创建日期',
      dataIndex: 'created_at',
      width: 110,
      render: v => v ? dayjs(v).format('YYYY-MM-DD') : ''
    }
  ];

  return (
    <div style={{ padding:24 }}>
      <h2 style={{ marginTop:0, marginBottom:16 }}>工作计划</h2>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        { key:'list', label:'计划列表与管理', children:(
          <div className='workplans-wrapper'>
            <div className='workplans-toolbar'>
              <DatePicker picker="month" value={dayjs(month+'-01')} onChange={(d)=> d && setMonth(d.format('YYYY-MM'))} />
              {users.length>0 && (
                <Select allowClear placeholder='按用户筛选' style={{ width:160 }} value={selectedUser} onChange={v=> setSelectedUser(v)} options={users.map(u=> ({ label:u.name, value:u.id }))} />
              )}
              <Input placeholder='关键字搜索' style={{ width:220 }} value={keyword} onChange={e=> setKeyword(e.target.value)} allowClear />
              <Button onClick={loadPlans} loading={loading}>刷新</Button>
              <Button onClick={exportExcel}>导出Excel</Button>
              {canManage && <Button type='primary' onClick={()=> setCreateVisible(true)}>新增计划</Button>}
              {selectedIds.length>0 && <Button type='primary' onClick={()=> setBulkVisible(true)}>批量进度更新({selectedIds.length})</Button>}
              {selectedIds.length>0 && <Button onClick={clearSelection}>清除选择</Button>}
            </div>
            <Space style={{ marginBottom:12 }} wrap>
              {summaryStats.overdue>0 && <Tag color='red'>逾期 {summaryStats.overdue}</Tag>}
              {summaryStats.dueSoon>0 && <Tag color='orange'>临近 {summaryStats.dueSoon}</Tag>}
              {summaryStats.overdue===0 && summaryStats.dueSoon===0 && <Tag color='green'>暂无临近或逾期</Tag>}
            </Space>
            {summaryStats.overdue>0 && <Alert type='error' showIcon message={`有 ${summaryStats.overdue} 条计划已逾期未完成`} style={{ marginBottom:12 }} />}
            {summaryStats.dueSoon>0 && <Alert type='warning' showIcon message={`有 ${summaryStats.dueSoon} 条计划即将到期`} style={{ marginBottom:12 }} />}
            <Table
              className='workplans-table'
              size='small'
              loading={loading}
              dataSource={tableData}
              rowKey='id'
              pagination={false}
              columns={columns}
              locale={{ emptyText: '暂无工作计划' }}
              scroll={{ x: true }}
              rowClassName={(record)=> record._overdue ? 'wp-row-overdue' : (record._dueSoon ? 'wp-row-duesoon' : '')}
            />
          </div>
        )},
        { key:'board', label:'完成情况看板', children:(
          <>
            <Space style={{ marginBottom:16 }}>
              <DatePicker picker='month' value={dayjs(month+'-01')} onChange={d=> d && setMonth(d.format('YYYY-MM'))} />
              <Button onClick={loadBoardData} loading={boardLoading}>刷新</Button>
              <Button onClick={async ()=>{
                try {
                  const res = await http.get('/work-plans/board-export', { params:{ month }, responseType:'blob' });
                  const blob = new Blob([res.data], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `work_plans_board_${month||'all'}.xlsx`; a.click();
                  URL.revokeObjectURL(url);
                } catch(e){ message.error(e.response?.data?.error || e.message || '导出失败'); }
              }}>导出看板Excel</Button>
            </Space>
            <Row gutter={16}>
              <Col span={6}><Card><Statistic title='计划数' value={boardStats.total} /></Card></Col>
              <Col span={6}><Card><Statistic title='已完成' value={boardStats.completed} valueStyle={{ color:'#3f8600' }} /></Card></Col>
              <Col span={6}><Card><Statistic title='逾期未完成' value={boardStats.overdue} valueStyle={{ color:'#cf1322' }} /></Card></Col>
              <Col span={6}><Card><Statistic title='平均进度%' value={boardStats.avg.toFixed(1)} /></Card></Col>
            </Row>
            <Row gutter={16} style={{ marginTop:24 }}>
              <Col span={14}>
                <Card title='按用户汇总' bodyStyle={{ padding:0 }}>
                  <Table size='small' columns={[{ title:'用户', dataIndex:'user_name' },{ title:'计划数', dataIndex:'count' },{ title:'已完成', dataIndex:'completed' },{ title:'平均进度%', dataIndex:'avg_progress', render:v=> v.toFixed(1) },{ title:'最迟计划日期', dataIndex:'max_date' }]} dataSource={boardPerUser} rowKey='user_name' pagination={false} />
                </Card>
              </Col>
              <Col span={10}>
                <Card title='进度最低的未完成计划 (Top 10)' bodyStyle={{ maxHeight:400, overflow:'auto' }}>
                  {boardTopIncomplete.map(r=>{
                    const prog = Number(r.progress_percent)||0;
                    const overdue = prog<100 && dayjs(r.planned_completion_date).isBefore(dayjs(),'day');
                    const dueSoon = prog<100 && !overdue && dayjs(r.planned_completion_date).isBefore(dayjs().add(4,'day'),'day');
                    return (
                      <div key={r.id} style={{ marginBottom:12, background:'#fff', padding:10, borderRadius:6, boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                          <span style={{ fontWeight:500 }}>{r.user_name}</span>
                          <span style={{ fontSize:12 }}>{dayjs(r.planned_completion_date).format('YYYY-MM-DD')}</span>
                        </div>
                        <div style={{ fontSize:12, color:'#555', margin:'4px 0' }}>{r.plan_text.slice(0,60)}{r.plan_text.length>60?'...':''}</div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ flex:1 }}><Progress percent={prog} size='small' status={prog>=100?'success':'active'} /></div>
                          {prog>=100 ? <Tag color='green'>完成</Tag> : overdue ? <Tag color='red'>逾期</Tag> : dueSoon ? <Tag color='orange'>临近</Tag> : <Tag>进行中</Tag>}
                        </div>
                      </div>
                    );
                  })}
                  {!boardTopIncomplete.length && <div style={{ color:'#999' }}>暂无未完成计划</div>}
                </Card>
              </Col>
            </Row>
          </>
        )},
        {
          key:'requests', 
          label:'申请管理', 
          children:(
            <div>
              <div style={{ marginBottom:16 }}>
                <Button onClick={loadRequests} loading={false}>刷新申请</Button>
              </div>
              <Table
                size='small'
                dataSource={requests}
                rowKey='id'
                pagination={false}
                columns={[
                  { title:'计划内容', dataIndex:'plan_text', ellipsis:true },
                  { title:'申请类型', dataIndex:'type', render: v=> v==='delay'?'延期':v==='cancel'?'取消':'转移' },
                  { title:'申请详情', dataIndex:'details', render: (d, r)=> {
                    if(r.type==='delay') return `新日期: ${d.new_date}`;
                    if(r.type==='transfer') return `转移给: ${users.find(u=>u.id===d.new_user_id)?.name||'未知'}`;
                    return '取消';
                  }},
                  { title:'申请人', dataIndex:'requester_name' },
                  { title:'状态', dataIndex:'status', render: v=> <Tag color={v==='pending'?'orange':'green'}>{v==='pending'?'待审':'已审'}</Tag> },
                  { title:'操作', dataIndex:'actions', render: (_, r)=> canApprove && r.status==='pending' ? (
                    <Space>
                      <Button size='small' type='primary' onClick={()=> setApprovalModal({ visible:true, requestId:r.id, action:'approve' })}>批准</Button>
                      <Button size='small' danger onClick={()=> setApprovalModal({ visible:true, requestId:r.id, action:'reject' })}>拒绝</Button>
                    </Space>
                  ) : null }
                ]}
                locale={{ emptyText: '暂无申请' }}
                scroll={{ x: true }}
              />
            </div>
          )
        }
      ]} />

      {/* 申请提交模态 */}
      <Modal open={requestModal.visible} title={`申请${requestModal.type==='delay'?'延期':requestModal.type==='cancel'?'取消':'转移'}`} onOk={submitRequest} onCancel={()=> setRequestModal({ visible: false, type: '', planId: null, details: { new_date: '', new_user_id: '' } })} okText='提交申请' destroyOnClose>
        <Space direction='vertical' style={{ width:'100%' }} size={12}>
          {requestModal.type==='delay' && (
            <div>
              <span>新完成日期:</span>
              <DatePicker value={requestModal.details.new_date ? dayjs(requestModal.details.new_date) : null} onChange={d=> setRequestModal(m=> ({ ...m, details: { ...m.details, new_date: d ? d.format('YYYY-MM-DD') : '' } }))} />
            </div>
          )}
          {requestModal.type==='transfer' && (
            <div>
              <span>转移给:</span>
              <Select style={{ width:200 }} value={requestModal.details.new_user_id} onChange={v=> setRequestModal(m=> ({ ...m, details: { ...m.details, new_user_id: v } }))} options={users.map(u=> ({ label:u.name, value:u.id }))} />
            </div>
          )}
          {requestModal.type==='cancel' && <div>确认取消此工作计划？</div>}
        </Space>
      </Modal>

      {/* 审核模态 */}
      <Modal open={approvalModal.visible} title='审核申请' onOk={()=> approveRequest(approvalModal.requestId, approvalModal.action)} onCancel={()=> setApprovalModal({ visible: false, requestId: null, action: '' })} okText={approvalModal.action==='approve'?'批准':'拒绝'} destroyOnClose>
        <div>确认{approvalModal.action==='approve'?'批准':'拒绝'}此申请？</div>
      </Modal>

      {/* 附件 Modal */}
      <Modal open={photoModal.open} title='计划附件' footer={null} onCancel={()=> setPhotoModal({ open:false, photos:[], planId:null })}>
        {photoModal.photos.length ? photoModal.photos.map(ph => (
          <div key={ph.id} style={{ marginBottom:8 }}>
            <button
              type="button"
              onClick={() => openWithAuth(ph.url)}
              style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1677ff' }}
            >
              {ph.file_name}
            </button>
          </div>
        )) : <div style={{ color:'#888' }}>暂无附件</div>}
      </Modal>

      {/* 新增计划弹窗 */}
      <Modal open={createVisible} title='新增工作计划' onOk={submitPlan} onCancel={()=> setCreateVisible(false)} okText='提交' confirmLoading={uploading} destroyOnClose>
        <Space direction='vertical' style={{ width:'100%' }} size={12}>
          <Input.TextArea rows={3} placeholder='工作计划内容' value={form.plan_text} onChange={e=> setForm(f=> ({ ...f, plan_text:e.target.value }))} />
          <Space wrap>
            <span>计划完成日期:</span>
            <DatePicker value={dayjs(form.planned_completion_date)} onChange={d=> d && setForm(f=> ({ ...f, planned_completion_date:d.format('YYYY-MM-DD') }))} />
            <span>进度%:</span>
            <InputNumber min={0} max={100} value={form.progress_percent} onChange={v=> setForm(f=> ({ ...f, progress_percent:v }))} />
          </Space>
          <Input.TextArea rows={2} placeholder='上月计划完成情况(可选)' value={form.last_month_completion} onChange={e=> setForm(f=> ({ ...f, last_month_completion:e.target.value }))} />
          <Input.TextArea rows={2} placeholder='备注(可选)' value={form.remark} onChange={e=> setForm(f=> ({ ...f, remark:e.target.value }))} />
          <Upload multiple fileList={uploadFiles} onChange={({ fileList })=> setUploadFiles(fileList)} beforeUpload={()=> false} listType='picture'>
            <Button>选择照片附件</Button>
          </Upload>
        </Space>
      </Modal>

      {/* 编辑计划弹窗 */}
      <Modal open={editVisible} title='编辑工作计划' onOk={saveEditPlan} onCancel={()=> setEditVisible(false)} okText='保存' confirmLoading={editUploading} destroyOnClose>
        <Space direction='vertical' style={{ width:'100%' }} size={12}>
          <Input.TextArea rows={3} placeholder='工作计划内容' value={editForm.plan_text} onChange={e=> setEditForm(f=> ({ ...f, plan_text:e.target.value }))} />
          <Space wrap>
            <span>计划完成日期:</span>
            <DatePicker value={dayjs(editForm.planned_completion_date)} onChange={d=> d && setEditForm(f=> ({ ...f, planned_completion_date:d.format('YYYY-MM-DD') }))} />
            <span>进度%:</span>
            <InputNumber min={0} max={100} value={editForm.progress_percent} onChange={v=> setEditForm(f=> ({ ...f, progress_percent:v }))} />
          </Space>
            <Input.TextArea rows={2} placeholder='上月计划完成情况(可选)' value={editForm.last_month_completion} onChange={e=> setEditForm(f=> ({ ...f, last_month_completion:e.target.value }))} />
            <Input.TextArea rows={2} placeholder='备注(可选)' value={editForm.remark} onChange={e=> setEditForm(f=> ({ ...f, remark:e.target.value }))} />
          <div style={{ fontSize:12, color:'#555' }}>已有附件:</div>
          <div style={{ maxHeight:120, overflow:'auto', padding:4, background:'#fafafa', border:'1px solid #eee', borderRadius:4 }}>
            {editPhotos.length ? editPhotos.map(ph => (
              <div key={ph.id}>
                <button
                  type="button"
                  onClick={() => openWithAuth(ph.url)}
                  style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1677ff' }}
                >
                  {ph.file_name}
                </button>
              </div>
            )) : <div style={{ color:'#999' }}>暂无附件</div>}
          </div>
          <Upload multiple fileList={editFiles} onChange={({ fileList })=> setEditFiles(fileList)} beforeUpload={()=> false} listType='picture'>
            <Button>添加新附件</Button>
          </Upload>
        </Space>
      </Modal>

      {/* 批量进度更新 */}
      <Drawer title='批量更新进度' open={bulkVisible} onClose={()=> setBulkVisible(false)} width={360}>
        <Space direction='vertical' style={{ width:'100%' }}>
          <div>选择模式:</div>
          <Select value={bulkMode} onChange={setBulkMode} options={[{value:'set',label:'设为值'},{value:'increment',label:'增加值'}]} />
          <div>数值:</div>
          <InputNumber min={0} max={500} value={bulkValue} onChange={v=> setBulkValue(v)} style={{ width:'100%' }} />
          {bulkMode==='set' && <div style={{ fontSize:12, color:'#888' }}>设为值: 直接覆盖为该百分比 (自动裁剪 0-100)</div>}
          {bulkMode==='increment' && <div style={{ fontSize:12, color:'#888' }}>增加值: 在现有基础上累加 (上限 100)</div>}
          <Button type='primary' onClick={doBulkUpdate}>执行批量更新</Button>
        </Space>
      </Drawer>
    </div>
  );
};

export default WorkPlansPage;